import { useWeb3React } from '@web3-react/core'
import { Address, L1ChainId, TokenId, Wei } from '../../types'
import { useSelector } from 'react-redux'
import { RootState } from '../index'
import { useEthereumTokenInfo } from '../../hooks/web3/useEthereumTokenInfo'
import { useMemo } from 'react'
import { StaticContracts } from './types'
import { useAccount } from '@starknet-react/core'
import { useChainType } from '../connect/hooks'
import { ChainType } from '../../constants/chains'
import { numberToHex } from '../../utils/number'
import { AccountInterface, num } from 'starknet'
import { Web3Provider } from '@ethersproject/providers'

export function useChainTypeValue<T>(evmValue: T, starknetValue: T): T {
  const chainType = useChainType()
  return chainType === ChainType.Starknet ? starknetValue : evmValue
}
export function useWeb3Provider() {
  const { account } = useAccount()
  const { provider } = useWeb3React()
  return useChainTypeValue<Web3Provider | AccountInterface | undefined>(
    provider,
    account,
  )
}
export function useEvmConnected() {
  const { isActive = false, account = '' } = useWeb3React()
  return isActive && !!account
}
export function useStarknetConnected() {
  const { isConnected } = useAccount()
  return !!isConnected
}
export function useWeb3Connected() {
  const evmConnected = useEvmConnected()
  const starknetConnected = useStarknetConnected()
  return useChainTypeValue<boolean>(evmConnected, starknetConnected)
}
export function useWeb3Connecting() {
  const { isActivating = false } = useWeb3React()
  const { isConnecting } = useAccount()
  return useChainTypeValue<boolean>(isActivating, !!isConnecting)
}
export function useWeb3Address() {
  const { account = '' } = useWeb3React()
  const { address = '' } = useAccount()
  return useChainTypeValue<Address>(account, address)
}
export function useWeb3ChainId() {
  const { chainId: evmChainId = 0 } = useWeb3React()
  const { chainId: starknetChainId = 0 } = useAccount()
  return useChainTypeValue<L1ChainId>(
    numberToHex(evmChainId),
    num.toHex(starknetChainId),
  )
}
export function useEvmTokenBalance(l1ChainId: L1ChainId, tokenId: TokenId) {
  const token = useEthereumTokenInfo(l1ChainId, tokenId)
  return useSelector<RootState, Wei | undefined>((state) => {
    try {
      return state.web3.ethereumBalances[l1ChainId][
        token?.address?.toLowerCase()!
      ]
    } catch (e) {
      return ''
    }
  })
}
export function useContracts(): StaticContracts {
  const contracts = useSelector<RootState, StaticContracts>(
    (state) => state.web3.contracts,
  )

  return useMemo(() => {
    if (contracts) {
      return contracts
    }
    return {
      multicall: {},
      faucet: {},
    }
  }, [contracts])
}
